import { Business } from "@mui/icons-material";
import Close from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
// import CustomAutocomplete from "components/helpers/CustomAutocomplete";
import { hoverStyle } from "components/helpers/MediaQueries";
import { states } from "components/helpers/ReusableLargeValues";
import useDebounce from "components/hooks/useDebounce";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchAgentsLeadsQuery } from "state/managementApi";
import { useCreateQuoteMutation } from "state/operationsApi";
import * as yup from 'yup';


const QuoteSubmissionForm = ({ open, closeForm, fromLead = false, lead = null }) => {
    const userData = useSelector((state) => state.user);
    const theme = useTheme();
    const [calculatedMargin, setCalculatedMargin] = useState(0);
    const [marginPercentage, setMarginPercentage] = useState(0);
    const [originStateValue, setOriginStateValue] = useState(null);
    const [originInputValue, setOriginInputValue] = useState("");
    const [destinationStateValue, setDestinationStateValue] = useState(null);
    const [destinationInputValue, setDestinationInputValue] = useState("");

    // Lead searching states
    const [selectedLeadId, setSelectedLeadId] = useState(null); // Selected option
    const [input, setInput] = useState(""); // Current search input
    const [options, setOptions] = useState([]); // List of search options
    const debouncedValue = useDebounce(input, 1000);

    const [createQuote] = useCreateQuoteMutation();
    const {
        data: searchResults = [],
        // isLoading: searchLoading,
        // refetch: refetchSearch,
        isSuccess
    } = useSearchAgentsLeadsQuery({ 
        userId: userData._id, 
        searchQuery: input, 
        page: 1, 
        limit: 10, 
        sortField: 'name', 
        sortOrder: 'asc', 
        isQuoteSelection: true 
    }, { skip: debouncedValue == "" || (input == "" || selectedLeadId) });

    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (searchResults && isSuccess) {
            setOptions(searchResults.leads);
        };
    }, [searchResults, isSuccess]);

    // Handlers
    const handleStateChanges = (value, state) => {
        if (value === "origin") {
            formik.setFieldValue("originState", state);
        } else if (value === "destination") {
            formik.setFieldValue("destinationState", state);
        };
    };

    const handleQuoteFormSubmission = async (values) => {
        const quote = {
            emailSubject: values.emailSubject,
            origin: {
                city: values.originCity,
                state: originStateValue.code
            },
            destination: {
                city: values.destinationCity,
                state: destinationStateValue.code
            },
            equipType: values.equipType,
            loadType: values.loadType,
            truckPrice: values.truckPrice,
            customerQuote: values.customerQuote,
            dispatcherName: values.dispatcherName,
            dispatcherPhone: values.dispatcherPhone,
            margin: calculatedMargin,
            driverName: values.driverName,
            driverPhone: values.driverPhone,
            pickupAndDeliveryETA: values.pickupAndDeliveryETA,
            shipmentDate: values.shipmentDate,
            truckInHand: values.truckInHand,
            carrierInfo: {
                mcNumber: values.carrierMcNumber,
                phoneNumber: values.carrierPhoneNumber,
                email: values.carrierEmail
            },
            notes: values.notes,
            quoteStatus: values.quoteStatus,
            status: values.quoteStatus === "won" ? "booking" : "quoting",
        };

        if (fromLead) {
            quote.lead = lead._id;
        }

        if (selectedLeadId) {
            quote.lead = selectedLeadId;
        }

        try {
            await createQuote(quote)
                .then(() => {
                    enqueueSnackbar("Quote submitted");
                    closeForm();
                })
        } catch (error) {
            console.error('Error creating quote:', error);
        }
    };


    const formik = useFormik({
        initialValues: {
            emailSubject: '',
            originCity: '',
            originState: '',
            destinationCity: '',
            destinationState: '',
            equipType: '',
            loadType: '',
            truckPrice: 0,
            customerQuote: 0,
            margin: 0,
            driverName: '',
            driverPhone: '',
            dispatcherName: '',
            dispatcherPhone: '',
            pickupAndDeliveryETA: '',
            shipmentDate: '',
            truckInHand: false,
            carrierMcNumber: '',
            carrierPhoneNumber: '',
            carrierEmail: '',
            notes: '',
            quoteStatus: 'unknown',
        },
        validationSchema: yup.object({
            emailSubject: yup.string().required('Email subject is required'),
            originCity: yup.string().required('Origin city is required'),
            originState: yup.string().required('Origin state is required'),
            destinationCity: yup.string().required('Destination city is required'),
            destinationState: yup.string().required('Destination state is required'),
            equipType: yup.string(),
            loadType: yup.string(),
            truckPrice: yup.number(),
            customerQuote: yup.number(),
            margin: yup.number(),
            driverName: yup.string(),
            driverPhone: yup.string(),
            dispatcherName: yup.string(),
            dispatcherPhone: yup.string(),
            pickupAndDeliveryETA: yup.string(),
            shipmentDate: yup.string(),
            truckInHand: yup.boolean(),
            carrierMcNumber: yup.string(),
            carrierPhoneNumber: yup.string(),
            carrierEmail: yup.string().email('Invalid email address'),
            status: yup.string(),
            assignedName: yup.string(),
            assignedEmail: yup.string(),
            notes: yup.string(),
            quoteStatus: yup.string(),
        }),
        onSubmit: values => {
            handleQuoteFormSubmission(values)
        },
    });

    const handleDecimalFormat = (value) => {
        return parseFloat(value).toFixed(2);
    };

    const handleQuoteChange = (e, value) => {
        let newMargin;

        if (value === "truckPrice") {
            formik.setFieldValue("truckPrice", e.target.value);
            newMargin = formik.values.customerQuote - e.target.value;
        } else if (value === "customerQuote") {
            formik.setFieldValue("customerQuote", e.target.value);
            newMargin = e.target.value - formik.values.truckPrice;
        };

        if (formik.values.truckPrice !== 0 && formik.values.customerQuote !== "0.00") {
            setCalculatedMargin((handleDecimalFormat(newMargin)));

            if (value === "truckPrice" && formik.values.truckPrice!== "0.00") {
                setMarginPercentage(handleDecimalFormat((newMargin / formik.values.customerQuote) * 100));
            } else if (value === "customerQuote" && formik.values.customerQuote !== 0) {
                setMarginPercentage(handleDecimalFormat((newMargin / e.target.value) * 100));
            };
        };
    };

    const handleInputChange = (e, value) => {
      setInput(value);
    };
    
    const handleChange = (e, value, reason) => {
        if (reason !== "clear") {
            setSelectedLeadId(value._id);
        } else {
            setSelectedLeadId(null);
        }
    };

    return (
        <Dialog open={open} onClose={closeForm} fullWidth maxWidth="lg">
            <DialogTitle>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Quote Submission Form</Typography>
                    <IconButton onClick={closeForm}>
                        <Close sx={hoverStyle} />
                    </IconButton>
                </div>
                <Divider sx={{ mb: '15px' }} />
            </DialogTitle>
            <DialogContent>
                <Container
                    component="main"
                    maxWidth="xl"
                    padding="40px"
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: theme.palette.primary[900],
                        padding: '20px',
                        margin: '0 auto',
                    }}
                >
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="emailSubject"
                                    label="Email Subject"
                                    name="emailSubject"
                                    autoFocus
                                    value={formik.values.emailSubject}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.emailSubject && formik.errors.emailSubject && (
                                    <Alert severity="error">{formik.errors.emailSubject}</Alert>
                                )}
                                <Divider sx={{ m: '15px 0px', }} />
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container item spacing={2}>
                                    <Grid item xs={2}>
                                        <Typography>
                                            Origin:
                                        </Typography>
                                        <Divider />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            id="originCity"
                                            label="City"
                                            name="originCity"
                                            required
                                            // autoFocus
                                            value={formik.values.originCity}
                                            onChange={formik.handleChange}
                                            multiline
                                        />
                                        {formik.touched.originCity && formik.errors.originCity && (
                                            <Alert severity="error">{formik.errors.originCity}</Alert>
                                        )}
                                    </Grid>
                                    <Grid item xs={4}>
                                        {/* <CustomAutocomplete 
                                            value={originStateValue}
                                            setValue={setOriginStateValue}
                                            inputVal={originInputValue}
                                            setInputVal={setOriginInputValue}
                                            options={states}
                                            handleChanges={handleStateChanges}
                                            formik={formik}
                                        /> */}
                                        <Autocomplete
                                            value={originStateValue}
                                            onChange={(e, newValue) => {
                                                setOriginStateValue(newValue);
                                                if (newValue) {
                                                    handleStateChanges("origin", newValue.code);
                                                } else {
                                                    formik.setFieldValue("originState", "");
                                                };
                                            }}
                                            inputValue={originInputValue}
                                            onInputChange={(e, newInputValue) => {
                                                setOriginInputValue(newInputValue);
                                            }}
                                            options={states}
                                            fullWidth
                                            autoHighlight
                                            autoSelect
                                            selectOnFocus
                                            includeInputInList
                                            // clearOnBlur
                                            // clearOnEscape
                                            handleHomeEndKeys
                                            getOptionLabel={(option) => option.label}
                                            isOptionEqualToValue={(option, value) => option.code === value.code}
                                            filterOptions={(options, { inputValue }) =>
                                                options.filter(option =>
                                                    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                    option.code.toLowerCase().includes(inputValue.toLowerCase())
                                                )
                                            }
                                            renderOption={(props, option) => (
                                                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                    {option.label} ({option.code})
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="State/Province"
                                                    margin="normal"
                                                    name="originState"
                                                    required
                                                    inputProps={{
                                                        ...params.inputProps,
                                                    }}
                                                />
                                            )}
                                        />
                                        {formik.touched.originState && formik.errors.originState && (
                                            <Alert severity="error">{formik.errors.originState}</Alert>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container item spacing={2}>
                                    <Grid item xs={2}>
                                        <Typography>
                                            Destination:
                                        </Typography>
                                        <Divider />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            id="destinationCity"
                                            label="City"
                                            name="destinationCity"
                                            // autoFocus
                                            required
                                            value={formik.values.destinationCity}
                                            onChange={formik.handleChange}
                                            multiline
                                        />
                                        {formik.touched.destinationCity && formik.errors.destinationCity && (
                                            <Alert severity="error">{formik.errors.destinationCity}</Alert>
                                        )}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Autocomplete
                                            value={destinationStateValue}
                                            onChange={(e, newValue) => {
                                                setDestinationStateValue(newValue);
                                                if (newValue) {
                                                    handleStateChanges("destination", newValue.code);
                                                } else {
                                                    formik.setFieldValue("destinationState", "");
                                                };
                                            }}
                                            inputValue={destinationInputValue}
                                            onInputChange={(e, newInputValue) => {
                                                setDestinationInputValue(newInputValue);
                                            }}
                                            options={states}
                                            autoHighlight
                                            autoSelect
                                            selectOnFocus
                                            // clearOnBlur
                                            // clearOnEscape
                                            handleHomeEndKeys
                                            getOptionLabel={(option) => option.label}
                                            isOptionEqualToValue={(option, value) => option.code === value.code}
                                            filterOptions={(options, { inputValue }) =>
                                                options.filter(option =>
                                                    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                    option.code.toLowerCase().includes(inputValue.toLowerCase())
                                                )
                                            }
                                            renderOption={(props, option) => (
                                                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                    {option.label} ({option.code})
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="State/Province"
                                                    margin="normal"
                                                    name="destinationState"
                                                    required
                                                    inputProps={{
                                                        ...params.inputProps,
                                                    }}
                                                />
                                            )}
                                        />
                                        {formik.touched.destinationState && formik.errors.destinationState && (
                                            <Alert severity="error">{formik.errors.destinationState}</Alert>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="quoteStatus"
                                    label="Quote Status"
                                    name="quoteStatus"
                                    // autoFocus
                                    value={formik.values.quoteStatus}
                                    onChange={formik.handleChange}
                                    select
                                >
                                    <MenuItem value="live">Live</MenuItem>
                                    <MenuItem value="not live">Not Live</MenuItem>
                                    <MenuItem value="won">Won</MenuItem>
                                    <MenuItem value="unknown">Unknown</MenuItem>
                                </TextField>
                                {formik.touched.quoteStatus && formik.errors.quoteStatus && (
                                    <Alert severity="error">{formik.errors.quoteStatus}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    margin="normal"
                                    fullWidth
                                    id="shipmentDate"
                                    label="Shipment Date"
                                    name="shipmentDate"
                                    // autoFocus
                                    value={formik.values.shipmentDate}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.shipmentDate && formik.errors.shipmentDate && (
                                    <Alert severity="error">{formik.errors.shipmentDate}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="equipType"
                                    label="Equipment Type"
                                    name="equipType"
                                    // autoFocus
                                    value={formik.values.equipType}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.equipType && formik.errors.equipType && (
                                    <Alert severity="error">{formik.errors.equipType}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="loadType"
                                    label="Load Type"
                                    name="loadType"
                                    // autoFocus
                                    value={formik.values.loadType}
                                    onChange={formik.handleChange}
                                    defaultValue=""
                                    select
                                >
                                    <MenuItem value="" style={{ display: 'none', }} />
                                    <MenuItem value="Full Truckload">
                                        Full Truckload
                                    </MenuItem>
                                    <MenuItem value="Partial">
                                        Partial
                                    </MenuItem>
                                    <MenuItem value="Doesn't Matter">
                                        Doesn't Matter
                                    </MenuItem>
                                </TextField>
                                {formik.touched.loadType && formik.errors.loadType && (
                                    <Alert severity="error">{formik.errors.loadType}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="truckPrice">Truck Price</InputLabel>
                                    <OutlinedInput
                                        id="truckPrice"
                                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                        label="Amount"
                                        name="truckPrice"
                                        // autoFocus
                                        type="number"
                                        value={formik.values.truckPrice}
                                        onChange={(e) => {
                                            handleQuoteChange(e, "truckPrice");
                                        }}
                                        onBlur={() => {
                                            if (formik.values.truckPrice !== "" || formik.values.truckPrice !== "0.00") {
                                                const newValue = handleDecimalFormat(formik.values.truckPrice);
                                                formik.setFieldValue("truckPrice", newValue);
                                            } else {
                                                formik.setFieldValue("truckPrice", 0);
                                            }
                                        }}
                                        onFocus={() => {
                                            if (formik.values.truckPrice === 0 || formik.values.truckPrice === "0.00") {
                                                formik.setFieldValue("truckPrice", "");
                                            }
                                        }}
                                        sx={{
                                            '& input[type=number]': {
                                                'MozAppearance': 'textfield',
                                            },
                                            '& input[type=number]::-webkit-outer-spin-button': {
                                                'WebkitAppearance': 'none',
                                                margin: 0,
                                            },
                                            '& input[type=number]::-webkit-inner-spin-button': {
                                                'WebkitAppearance': 'none',
                                                margin: 0,
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="customerQuote">Customer Quote</InputLabel>
                                    <OutlinedInput
                                        id="customerQuote"
                                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                        label="Amount"
                                        name="customerQuote"
                                        // autoFocus
                                        type="number"
                                        value={formik.values.customerQuote}
                                        onChange={(e) => {
                                            handleQuoteChange(e, "customerQuote");
                                        }}
                                        onBlur={() => {
                                            if (formik.values.customerQuote !== "" || formik.values.customerQuote !== "0.00") {
                                                const newValue = handleDecimalFormat(formik.values.customerQuote);
                                                formik.setFieldValue("customerQuote", newValue);
                                            } else {
                                                formik.setFieldValue("customerQuote", 0);
                                            }
                                        }}
                                        onFocus={() => {
                                            if (formik.values.customerQuote === 0 || formik.values.customerQuote === "0.00") {
                                                formik.setFieldValue("customerQuote", "");
                                            }
                                        }}
                                        sx={{
                                            '& input[type=number]': {
                                                'MozAppearance': 'textfield',
                                            },
                                            '& input[type=number]::-webkit-outer-spin-button': {
                                                'WebkitAppearance': 'none',
                                                margin: 0,
                                            },
                                            '& input[type=number]::-webkit-inner-spin-button': {
                                                'WebkitAppearance': 'none',
                                                margin: 0,
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="margin">Calculated Margin</InputLabel>
                                    <OutlinedInput
                                        id="margin"
                                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                        label="Amount"
                                        name="margin"
                                        // autoFocus
                                        value={calculatedMargin}
                                        disabled
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={1.5}>
                                <FormControl>
                                    <InputLabel htmlFor="margin">Margin %</InputLabel>
                                    <OutlinedInput
                                        id="margin"
                                        endAdornment={<InputAdornment position="start">%</InputAdornment>}
                                        label="Percentage"
                                        name="percentage"
                                        // autoFocus
                                        value={marginPercentage || 0}
                                        disabled
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={4.5} />
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="carrierMcNumber"
                                    label="MC Number"
                                    name="carrierMcNumber"
                                    // autoFocus
                                    value={formik.values.carrierMcNumber}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.carrierMcNumber && formik.errors.carrierMcNumber && (
                                    <Alert severity="error">{formik.errors.carrierMcNumber}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6} />
                            <Grid item xs={6}>
                                <TextField 
                                    margin="normal"
                                    fullWidth
                                    id="dispatcherName"
                                    label="Dispatcher Name"
                                    name="dispatcherName"
                                    // autoFocus
                                    value={formik.values.dispatcherName}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.dispatcherName && formik.errors.dispatcherName && (
                                    <Alert severity="error">{formik.errors.dispatcherName}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    margin="normal"
                                    fullWidth
                                    id="dispatcherPhone"
                                    label="Dispatcher Phone Number"
                                    name="dispatcherPhone"
                                    // autoFocus
                                    value={formik.values.dispatcherPhone}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.dispatcherPhone && formik.errors.dispatcherPhone && (
                                    <Alert severity="error">{formik.errors.dispatcherPhone}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="carrierEmail"
                                    label="Dispatcher Email"
                                    name="carrierEmail"
                                    // autoFocus
                                    value={formik.values.carrierEmail}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.carrierEmail && formik.errors.carrierEmail && (
                                    <Alert severity="error">{formik.errors.carrierEmail}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6} />
                            <Grid item xs={6}>
                                <TextField 
                                    margin="normal"
                                    fullWidth
                                    id="driverName"
                                    label="Driver Name"
                                    name="driverName"
                                    // autoFocus
                                    value={formik.values.driverName}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.driverName && formik.errors.driverName && (
                                    <Alert severity="error">{formik.errors.driverName}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    margin="normal"
                                    fullWidth
                                    id="driverPhone"
                                    label="Driver Phone Number"
                                    name="driverPhone"
                                    // autoFocus
                                    value={formik.values.driverPhone}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.driverPhone && formik.errors.driverPhone && (
                                    <Alert severity="error">{formik.errors.driverPhone}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="pickupAndDeliveryETA"
                                    label="Pickup and Delivery ETA"
                                    name="pickupAndDeliveryETA"
                                    // autoFocus
                                    value={formik.values.pickupAndDeliveryETA}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.pickupAndDeliveryETA && formik.errors.pickupAndDeliveryETA && (
                                    <Alert severity="error">{formik.errors.pickupAndDeliveryETA}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6} />
                            <Grid item xs={6}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                id="truckInHand"
                                                name="truckInHand"
                                                checked={formik.values.truckInHand}
                                                onChange={() => {
                                                    formik.setFieldValue("truckInHand", !formik.values.truckInHand);
                                                }}
                                            />
                                        }
                                        label="Truck In Hand"
                                        sx={{ pt: '21px', }}
                                    />
                                </FormGroup>
                            </Grid>
                            {!fromLead && 
                                <Grid item xs={6}>
                                    <Autocomplete 
                                        id="quote-lead-search"
                                        freeSolo
                                        clearOnEscape
                                        getOptionLabel={(option) => option.name}
                                        getOptionKey={(option) => option._id}
                                        filterOptions={(x) => x}
                                        options={options}
                                        inputValue={input}
                                        noOptionsText="No leads found" // Not working
                                        onChange={(e, newValue, reason) => {
                                            handleChange(e, newValue, reason);
                                        }}
                                        onInputChange={(e, newInput) => {
                                            handleInputChange(e, newInput);
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="(Optional) Find Matching Lead" fullWidth/>
                                        )}
                                        renderOption={(props, option, { inputValue }) => {
                                            const matches = match(option.name, inputValue, { insideWords: true });
                                            const parts = parse(option.name, matches);

                                            return (
                                                <li {...props}>
                                                    <Grid container alignItems={"center"}>
                                                        <Grid item sx={{ display: 'flex', width: 44 }}>
                                                            <Business />
                                                        </Grid>
                                                        <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word', }}>
                                                            {parts.map((part, index) => (
                                                                <Box
                                                                    key={index}
                                                                    component="span"
                                                                    sx={{ fontWeight: part.highlight ? 700 : 400, }}
                                                                >
                                                                    {part.text}
                                                                </Box>
                                                            ))}
                                                        </Grid>
                                                    </Grid>
                                                </li>
                                            )
                                        }}
                                    />
                                </Grid>
                            }
                            <Grid item xs={12}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="notes"
                                    label="Notes for Operations"
                                    name="notes"
                                    multiline
                                    rows={4}
                                    // autoFocus
                                    value={formik.values.notes}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.notes && formik.errors.notes && (
                                    <Alert severity="error">{formik.errors.notes}</Alert>
                                )}
                            </Grid>
                        </Grid>
                    </form>
                </Container>
            </DialogContent>
            <DialogActions>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={formik.handleSubmit}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default QuoteSubmissionForm;